import {
  FileText,
  Bot,
  BarChart3,
  Settings,
  Send,
  CheckCircle2,
  TrendingUp,
  LineChart,
  Ship,
  BadgeDollarSign,
  type LucideIcon,
} from 'lucide-react';

// ── Features ──

export interface FeatureItem {
  icon: LucideIcon;
  title: string;
  description: string;
  gradient: string;
}

export const features: FeatureItem[] = [
  {
    icon: FileText,
    title: 'Cotizaciones Inteligentes',
    description:
      'Crea cotizaciones profesionales con modelos de pricing, aprobaciones automáticas y cálculos en tiempo real.',
    gradient: 'from-blue-500 to-cyan-400',
  },
  {
    icon: LineChart,
    title: 'Pricing Dinámico',
    description:
      'Monitorea precios de competidores en e-commerce y ajusta tu estrategia de precios automáticamente.',
    gradient: 'from-teal-500 to-emerald-400',
  },
  {
    icon: Ship,
    title: 'Seguimiento de Importaciones',
    description:
      'Rastrea costos de importación, aranceles, fletes e incoterms. Actualiza precios de costo en tiempo real.',
    gradient: 'from-indigo-500 to-blue-400',
  },
  {
    icon: Bot,
    title: 'Asistente IA',
    description:
      'IA integrada que analiza márgenes, sugiere estrategias de pricing y responde consultas de tu equipo.',
    gradient: 'from-orange-500 to-amber-400',
  },
  {
    icon: BarChart3,
    title: 'Analytics Avanzado',
    description: 'Dashboards con win rate, márgenes, tendencias por vendedor y línea de negocio.',
    gradient: 'from-pink-500 to-rose-400',
  },
  {
    icon: BadgeDollarSign,
    title: 'Multi-moneda',
    description: 'Cotiza en cualquier moneda con tipos de cambio actualizados automáticamente.',
    gradient: 'from-lime-500 to-green-400',
  },
];

// ── How it works ──

export interface StepItem {
  number: number;
  icon: LucideIcon;
  title: string;
  description: string;
}

export const steps: StepItem[] = [
  {
    number: 1,
    icon: Settings,
    title: 'Configura',
    description: 'Define tus modelos de pricing, materiales, monedas e impuestos.',
  },
  {
    number: 2,
    icon: Send,
    title: 'Cotiza',
    description: 'Crea cotizaciones con múltiples secciones y cálculos automáticos.',
  },
  {
    number: 3,
    icon: CheckCircle2,
    title: 'Aprueba',
    description: 'El workflow de aprobación notifica y gestiona el proceso.',
  },
  {
    number: 4,
    icon: TrendingUp,
    title: 'Analiza',
    description: 'Dashboards y la IA te dan insights para mejorar tu pricing.',
  },
];

// ── Stats ──

export interface StatItem {
  value: number;
  suffix: string;
  label: string;
}

export const stats: StatItem[] = [
  { value: 85, suffix: '%', label: 'Menos tiempo cotizando' },
  { value: 4, suffix: 'x', label: 'Más rápido que Excel' },
  { value: 32, suffix: '%', label: 'Mejora en márgenes' },
  { value: 0, suffix: '', label: 'Errores de cálculo' },
];

// ── AI Chat mock ──

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const aiChatMessages: ChatMessage[] = [
  {
    role: 'user',
    content: '¿Cuál es el margen promedio de mis cotizaciones este mes?',
  },
  {
    role: 'assistant',
    content:
      'Tu margen promedio este mes es del 32.5%, un 4% más que el mes anterior. Las cotizaciones de infraestructura TI tienen el mayor margen (38%) mientras que licenciamiento está en 24%. ¿Te gustaría ver un desglose por cliente?',
  },
  {
    role: 'user',
    content: 'Sí, muéstrame los top 3 clientes por margen.',
  },
  {
    role: 'assistant',
    content:
      'Top 3 clientes por margen:\n1. BNB — 42% (infraestructura)\n2. YPFB — 36% (seguridad TI)\n3. COTAS — 33% (servicios profesionales)\n\nBNB tiene un potencial de upsell en licenciamiento que podría subir al 45%.',
  },
];

// ── AI Capabilities ──

export const aiCapabilities = [
  'Análisis de márgenes y rentabilidad',
  'Sugerencias de pricing basadas en datos',
  'Búsqueda inteligente de cotizaciones',
  'Comparación de propuestas',
  'Detección de anomalías en precios',
];

// ── Pricing tiers ──

export interface PricingTier {
  name: string;
  description: string;
  features: string[];
  cta: string;
  highlighted?: boolean;
  badge?: string;
  usersIncluded: number;
}

export const pricingTiers: PricingTier[] = [
  {
    name: 'Starter',
    description: 'Para equipos pequeños con ventas directas y catálogos simples.',
    usersIncluded: 5,
    features: [
      'Hasta 5 usuarios',
      'Cotizaciones ilimitadas',
      '3 modelos de pricing',
      'Asistente IA básico',
      'Catálogo de productos',
      'Multi-moneda',
      'PDF con branding',
      'Soporte por email',
    ],
    cta: 'Solicitar demo',
  },
  {
    name: 'Pro',
    description: 'Para empresas con cotizaciones complejas y flujos de aprobación.',
    usersIncluded: 15,
    features: [
      'Hasta 15 usuarios',
      'Cotizaciones ilimitadas',
      'Modelos ilimitados',
      'Asistente IA completo (Powered by Claude)',
      'Workflow de aprobaciones',
      'Analytics avanzado',
      'Integraciones API',
      'Pricing dinámico básico',
      'Soporte prioritario',
    ],
    cta: 'Solicitar demo',
    highlighted: true,
    badge: 'Más elegido',
  },
  {
    name: 'Enterprise',
    description: 'Para organizaciones grandes con múltiples sedes y equipos.',
    usersIncluded: 30,
    features: [
      'Usuarios a medida',
      'Todo lo del plan Pro',
      'Multi-sucursal',
      'IA personalizada',
      'Pricing dinámico avanzado',
      'API completa + webhooks',
      'Capacitación presencial incluida',
      'Soporte 24/7 + SLA 99.5%',
      'Account manager dedicado',
    ],
    cta: 'Hablar con ventas',
  },
];

// ── Professional Services ──

export interface ProfessionalService {
  name: string;
  price: string;
  description: string;
}

export const professionalServices: ProfessionalService[] = [
  {
    name: 'Setup + Configuración',
    price: '$500',
    description:
      'Instalación, configuración de catálogo, márgenes y usuarios. Incluido en Pro y Enterprise.',
  },
  {
    name: 'Capacitación',
    price: 'Desde $300',
    description: 'Remoto ($300) o presencial ($600). Presencial incluida en Enterprise.',
  },
  {
    name: 'Migración de datos',
    price: '$200',
    description: 'Importamos tus catálogos, clientes y datos desde Excel o tu ERP.',
  },
  {
    name: 'Integración ERP/CRM',
    price: 'Desde $800',
    description: 'Conectamos Prixia con SAP, Oracle, Salesforce u otro sistema.',
  },
  {
    name: 'IA personalizada',
    price: '$500',
    description: 'Entrenamos la IA con tus datos históricos. Incluido en Enterprise.',
  },
];

// ── Footer ──

export interface FooterColumn {
  title: string;
  links: { label: string; href: string }[];
}

export const footerColumns: FooterColumn[] = [
  {
    title: 'Producto',
    links: [
      { label: 'Características', href: '#features' },
      { label: 'Pricing dinámico', href: '#dynamic-pricing' },
      { label: 'Importaciones', href: '#imports' },
      { label: 'Pricing', href: '#pricing' },
    ],
  },
  {
    title: 'Recursos',
    links: [
      { label: 'Deck comercial', href: '/prixia-pricing-deck.html' },
      { label: 'FAQ', href: '#faq' },
      // Vault 346 (D24): el contacto es el formulario (llega a Plataforma › Solicitudes).
      { label: 'Contacto', href: '/contact' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacidad', href: '/privacy' },
      { label: 'Términos', href: '/terms' },
    ],
  },
];

// ── Navbar ──

export interface NavDropdownItem {
  label: string;
  href: string;
  icon?: string;
}

export interface NavDropdownGroup {
  title: string;
  items: NavDropdownItem[];
}

export interface NavLink {
  label: string;
  href: string;
  dropdown?: NavDropdownGroup[];
}

// ── Logo cloud ──

export const logoCloudCompanies = [
  'BNB',
  'YPFB',
  'COTAS',
  'Tigo',
  'Entel',
  'Fancesa',
  'PIL Andina',
  'Softel',
  'CBN',
  'Embol',
  'Deloitte',
  'KPMG',
];

// ── Integrations ──

export interface IntegrationItem {
  name: string;
  category: string;
  color: string;
  initials: string;
}

export const integrations: IntegrationItem[] = [
  {
    name: 'SAP Business One',
    category: 'ERP',
    color: 'from-blue-600 to-blue-500',
    initials: 'SAP',
  },
  { name: 'SAP C4C', category: 'CRM', color: 'from-sky-600 to-sky-500', initials: 'C4C' },
  { name: 'Odoo', category: 'ERP', color: 'from-violet-600 to-violet-500', initials: 'OD' },
  { name: 'Salesforce', category: 'CRM', color: 'from-cyan-600 to-cyan-500', initials: 'SF' },
  { name: 'HubSpot', category: 'CRM', color: 'from-orange-600 to-orange-500', initials: 'HS' },
  { name: 'Shopify', category: 'E-commerce', color: 'from-green-600 to-green-500', initials: 'SH' },
  { name: 'VTEX', category: 'E-commerce', color: 'from-pink-600 to-pink-500', initials: 'VT' },
  { name: 'Magento', category: 'E-commerce', color: 'from-amber-600 to-amber-500', initials: 'MG' },
  { name: 'API REST', category: 'Custom', color: 'from-gray-600 to-gray-500', initials: '{ }' },
];

// ── Industries ──

export interface IndustryItem {
  slug: string;
  name: string;
  shortName: string;
  tagline: string;
  gradient: string;
}

export const industries: IndustryItem[] = [
  {
    slug: 'distribuidoras',
    name: 'Distribuidoras e Importadoras',
    shortName: 'Distribuidoras',
    tagline: 'Controla costos de importación y cotiza con márgenes reales.',
    gradient: 'from-blue-500 to-cyan-400',
  },
  {
    slug: 'tecnologia',
    name: 'Tecnología y Reventa',
    shortName: 'Tecnología',
    tagline: 'Cotizaciones multi-componente con pricing de licencias y hardware.',
    gradient: 'from-violet-500 to-purple-400',
  },
  {
    slug: 'manufactura',
    name: 'Manufactura',
    shortName: 'Manufactura',
    tagline: 'Fórmulas de pricing con variables de producción y materia prima.',
    gradient: 'from-emerald-500 to-green-400',
  },
  {
    slug: 'mayoristas',
    name: 'Comercio Mayorista',
    shortName: 'Mayoristas',
    tagline: 'Pricing por volumen, descuentos controlados y monitoreo e-commerce.',
    gradient: 'from-orange-500 to-amber-400',
  },
];

// ── Navbar ──

export const navLinks: NavLink[] = [
  {
    label: 'Producto',
    href: '/platform',
    dropdown: [
      {
        title: 'Plataforma',
        items: [
          { label: 'Overview', href: '/platform', icon: 'Layers' },
          { label: 'Cotizaciones', href: '#product-showcase', icon: 'FileText' },
          { label: 'Pricing Dinámico', href: '#dynamic-pricing', icon: 'LineChart' },
          { label: 'Importaciones', href: '#imports', icon: 'Ship' },
        ],
      },
      {
        title: 'Capacidades',
        items: [
          { label: 'Analytics', href: '#control', icon: 'BarChart3' },
          { label: 'Asistente IA', href: '#control', icon: 'Bot' },
          { label: 'Multi-moneda', href: '/platform', icon: 'BadgeDollarSign' },
        ],
      },
    ],
  },
  { label: 'Pricing', href: '#pricing' },
  {
    label: 'Recursos',
    href: '/resources',
    dropdown: [
      {
        title: 'Ventas',
        items: [
          { label: 'Guía de Producto', href: '/docs/product-guide', icon: 'BookOpen' },
          { label: 'ROI y Beneficios', href: '/docs/roi', icon: 'TrendingUp' },
          { label: 'Brochure de Ventas', href: '/sales-pdf', icon: 'FileDown' },
        ],
      },
      {
        title: 'Soporte',
        items: [
          { label: 'Centro de Ayuda', href: '/docs/help', icon: 'HelpCircle' },
          { label: 'Guía Rápida', href: '/docs/quick-start', icon: 'Rocket' },
          { label: 'FAQ', href: '/docs/faq', icon: 'MessageCircle' },
        ],
      },
    ],
  },
];
